import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
} from '@angular/core';

import { GrTooltipDirective } from '@components/overlays';

import type { GrCanvasItem, GrCanvasResizeEvent, GrCanvasResizeHandle } from '../canvas/canvas.types';
import { type ResizeSession } from './canvas-item.types';

const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_MAX_SPEED_PX = 18;

@Component({
  selector: 'gr-canvas-item',
  imports: [GrTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'gr-canvas-item relative flex min-h-0 min-w-0',
    '[class.gr-canvas-item--editing]': 'editing()',
    '[class.gr-canvas-item--resizing]': '!!resizeSession',
    '[class.gr-canvas-item--dragging]': 'dragging()',
    '[style.grid-column]': 'gridColumn()',
    '[style.grid-row]': 'gridRow()',
    '[style.position]': "dragging() ? 'fixed' : null",
    '[style.left.px]': 'floatLeft()',
    '[style.top.px]': 'floatTop()',
    '[style.width.px]': 'floatWidth()',
    '[style.height.px]': 'floatHeight()',
    '[style.z-index]': "dragging() ? 1000 : null",
  },
  templateUrl: './canvas-item.component.html',
})
export class GrCanvasItemComponent {
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly item = input.required<GrCanvasItem>();
  readonly editing = input<boolean>(false);
  readonly maxCols = input<number>(12);
  readonly minColSpan = input<number>(1);
  readonly minRowSpan = input<number>(1);
  readonly maxRowSpan = input<number>(24);
  readonly displayColSpan = input<number | null>(null);
  readonly displayRowSpan = input<number | null>(null);
  readonly resizable = input<boolean>(true);

  readonly placed = input<boolean>(true);
  readonly dragging = input<boolean>(false);
  readonly floatRect = input<{
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
  } | null>(null);

  readonly editRequested = output<{ id: string; x: number; y: number }>();
  readonly removeRequested = output<string>();
  readonly resized = output<GrCanvasResizeEvent>();
  readonly dragStart = output<{ id: string; event: PointerEvent; host: HTMLElement }>();

  protected readonly gridColumn = computed<string | null>(() => {
    if (this.dragging()) return null;
    if (this.placed()) return `${(this.item().x ?? 0) + 1} / span ${this.item().colSpan}`;
    return `span ${this.displayColSpan() ?? this.item().colSpan}`;
  });

  protected readonly gridRow = computed<string | null>(() => {
    if (this.dragging()) return null;
    if (this.placed()) return `${(this.item().y ?? 0) + 1} / span ${this.item().rowSpan}`;
    return `span ${this.displayRowSpan() ?? this.item().rowSpan}`;
  });

  protected readonly floatLeft = computed<number | null>(() =>
    this.dragging() ? (this.floatRect()?.left ?? null) : null,
  );
  protected readonly floatTop = computed<number | null>(() =>
    this.dragging() ? (this.floatRect()?.top ?? null) : null,
  );
  protected readonly floatWidth = computed<number | null>(() =>
    this.dragging() ? (this.floatRect()?.width ?? null) : null,
  );
  protected readonly floatHeight = computed<number | null>(() =>
    this.dragging() ? (this.floatRect()?.height ?? null) : null,
  );

  protected resizeSession: ResizeSession | null = null;
  private lastPointerClientX = 0;
  private lastPointerClientY = 0;
  private autoScrollRaf: number | null = null;

  protected onDragHandlePointerDown(event: PointerEvent): void {
    if (!this.editing() || !this.placed() || event.button !== 0) return;
    this.dragStart.emit({
      id: this.item().id,
      event,
      host: this.hostRef.nativeElement,
    });
  }

  protected onEdit(event: MouseEvent): void {
    this.editRequested.emit({ id: this.item().id, x: event.clientX, y: event.clientY });
  }

  protected onRemove(): void {
    this.removeRequested.emit(this.item().id);
  }

  protected startResize(handle: GrCanvasResizeHandle, event: PointerEvent): void {
    if (!this.editing() || !this.resizable()) return;
    event.preventDefault();
    event.stopPropagation();

    const gridEl = this.hostRef.nativeElement.parentElement;
    if (!gridEl) return;

    const gridStyle = getComputedStyle(gridEl);
    const cellWidth = this.measureCellWidth(gridEl, gridStyle);
    const rowHeight = this.measureRowHeight(gridStyle);
    const scrollEl = this.findScrollableAncestor(gridEl);
    const startScrollTop = scrollEl?.scrollTop ?? 0;
    const startMaxScrollTop = scrollEl
      ? Math.max(startScrollTop, scrollEl.scrollHeight - scrollEl.clientHeight)
      : startScrollTop;

    this.resizeSession = {
      handle,
      startColSpan: this.item().colSpan,
      startRowSpan: this.item().rowSpan,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollTop,
      startMaxScrollTop,
      scrollEl,
      cellWidth,
      rowHeight,
      maxCols: this.maxCols(),
    };
    this.lastPointerClientX = event.clientX;
    this.lastPointerClientY = event.clientY;

    (event.target as Element).setPointerCapture?.(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  protected onPointerMove(event: PointerEvent): void {
    const session = this.resizeSession;
    if (!session) return;

    this.lastPointerClientX = event.clientX;
    this.lastPointerClientY = event.clientY;

    this.applyResize(session);

    if (session.handle !== 'right') this.ensureAutoScroll();
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  protected endResize(): void {
    this.resizeSession = null;
    if (this.autoScrollRaf !== null) {
      cancelAnimationFrame(this.autoScrollRaf);
      this.autoScrollRaf = null;
    }
  }

  private applyResize(session: ResizeSession): void {
    const dx = this.lastPointerClientX - session.startClientX;
    const scrollDelta = (session.scrollEl?.scrollTop ?? 0) - session.startScrollTop;
    const dy = this.lastPointerClientY - session.startClientY + scrollDelta;

    let colSpan = session.startColSpan;
    let rowSpan = session.startRowSpan;

    if (session.handle === 'right' || session.handle === 'corner') {
      const colsDelta = Math.round(dx / session.cellWidth);
      colSpan = this.clamp(session.startColSpan + colsDelta, this.minColSpan(), session.maxCols);
    }
    if (session.handle === 'bottom' || session.handle === 'corner') {
      const rowsDelta = Math.round(dy / session.rowHeight);
      rowSpan = this.clamp(session.startRowSpan + rowsDelta, this.minRowSpan(), this.maxRowSpan());
    }

    if (colSpan !== this.item().colSpan || rowSpan !== this.item().rowSpan) {
      this.resized.emit({ id: this.item().id, colSpan, rowSpan });
    }
  }

  private ensureAutoScroll(): void {
    if (this.autoScrollRaf !== null) return;
    const tick = () => {
      const session = this.resizeSession;
      if (!session || !session.scrollEl) {
        this.autoScrollRaf = null;
        return;
      }
      const scrollEl = session.scrollEl;
      const rect =
        scrollEl === document.scrollingElement || scrollEl === document.documentElement
          ? { top: 0, bottom: window.innerHeight }
          : scrollEl.getBoundingClientRect();
      const distanceFromBottom = rect.bottom - this.lastPointerClientY;
      const distanceFromTop = this.lastPointerClientY - rect.top;
      let speed = 0;
      if (distanceFromBottom < AUTO_SCROLL_EDGE_PX) {
        speed =
          AUTO_SCROLL_MAX_SPEED_PX *
          (1 - Math.max(0, distanceFromBottom) / AUTO_SCROLL_EDGE_PX);
      } else if (distanceFromTop < AUTO_SCROLL_EDGE_PX) {
        speed =
          -AUTO_SCROLL_MAX_SPEED_PX *
          (1 - Math.max(0, distanceFromTop) / AUTO_SCROLL_EDGE_PX);
      }
      if (speed !== 0) {
        const before = scrollEl.scrollTop;
        const maxScrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
        const next = speed > 0
          ? Math.min(before + speed, maxScrollTop)
          : Math.max(before + speed, 0);
        if (next !== before) {
          scrollEl.scrollTop = next;
          this.applyResize(session);
        }
      }
      this.autoScrollRaf = requestAnimationFrame(tick);
    };
    this.autoScrollRaf = requestAnimationFrame(tick);
  }

  private findScrollableAncestor(start: HTMLElement): HTMLElement {
    let el: HTMLElement | null = start.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
      el = el.parentElement;
    }
    return document.documentElement;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private measureCellWidth(gridEl: HTMLElement, gridStyle: CSSStyleDeclaration): number {
    const cols = this.maxCols();
    const gapPx = this.parsePx(gridStyle.columnGap || gridStyle.gap || '0');
    const innerWidth =
      gridEl.clientWidth -
      this.parsePx(gridStyle.paddingLeft) -
      this.parsePx(gridStyle.paddingRight);
    const totalGap = gapPx * (cols - 1);
    return Math.max(1, (innerWidth - totalGap) / cols);
  }

  private measureRowHeight(gridStyle: CSSStyleDeclaration): number {
    const rowsRaw = gridStyle.gridAutoRows || '5rem';
    return this.parsePx(rowsRaw) || 80;
  }

  private parsePx(value: string): number {
    if (!value) return 0;
    if (value.endsWith('px')) return parseFloat(value);
    if (value.endsWith('rem')) {
      const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return parseFloat(value) * root;
    }
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
}
